import React, { useRef, memo } from 'react';
import useWindowSize from 'react-use/lib/useWindowSize';
import styled, { keyframes } from 'styled-components';

import { useElementResize } from 'hooks';
import HeaderButtons from './HeaderButtons';

function Windows({
  apps,
  onMouseDown,
  onClose,
  onMinimize,
  onMaximize,
  focusedAppId,
}) {
  return (
    <div style={{ position: 'relative', zIndex: 0 }}>
      {apps.map(app => (
        <StyledWindow
          show={!app.minimized}
          key={app.id}
          id={app.id}
          onMouseDown={onMouseDown}
          onMouseUpClose={onClose}
          onMouseUpMinimize={onMinimize}
          onMouseUpMaximize={onMaximize}
          isFocus={focusedAppId === app.id} // for styledWindow
          {...app}
        />
      ))}
    </div>
  );
}

const Window = memo(function({
  injectProps,
  id,
  onMouseDown,
  onMouseUpClose,
  onMouseUpMinimize,
  onMouseUpMaximize,
  header,
  defaultSize,
  defaultOffset,
  resizable,
  maximized,
  component,
  zIndex,
  isFocus,
  className,
  closeAsMinimize,
}) {
  function _onMouseDown() {
    onMouseDown(id);
  }
  function _onMouseUpClose() {
    onMouseUpClose(id);
  }
  function _onMouseUpMinimize() {
    onMouseUpMinimize(id);
  }
  const _onCloseOrMinimize = closeAsMinimize
    ? _onMouseUpMinimize
    : _onMouseUpClose;
  function _onMouseUpMaximize() {
    if (resizable) onMouseUpMaximize(id);
  }
  function onDoubleClickHeader(e) {
    if (e.target !== dragRef.current) return;
    _onMouseUpMaximize();
  }
  const dragRef = useRef(null);
  const skinDragRef = useRef(null);
  const ref = useRef(null);
  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const padding = 10000;
  const { offset, size } = useElementResize(ref, {
    dragRef: header?.invisible ? skinDragRef : dragRef,
    defaultOffset,
    defaultSize,
    boundary: {
      top: -padding,
      right: windowWidth + padding,
      bottom: windowHeight + padding,
      left: -padding,
    },
    resizable,
    resizeThreshold: 10,
  });
  let width, height, x, y;
  if (maximized) {
    width = windowWidth + 6;
    height = windowHeight - 24;
    x = -3;
    y = -3;
  } else {
    width = size.width;
    height = size.height;
    x = offset.x;
    y = offset.y;
  }
  return (
    <div
      className={className}
      ref={ref}
      onMouseDown={_onMouseDown}
      style={{
        transform: `translate(${x}px,${y}px)`,
        width: width ? `${width}px` : 'auto',
        height: height ? `${height}px` : 'auto',
        zIndex,
      }}
    >
      {!header?.invisible && <div className="header__bg" />}
      {!header?.invisible && (
        <header
          className="app__header"
          ref={dragRef}
          onDoubleClick={onDoubleClickHeader}
        >
          <img
            onDoubleClick={_onCloseOrMinimize}
            src={header.icon}
            alt={header.title}
            className="app__header__icon"
            draggable={false}
          />
          <div className="app__header__title">{header.title}</div>
          <HeaderButtons
            buttons={header.buttons}
            onMaximize={_onMouseUpMaximize}
            onMinimize={_onMouseUpMinimize}
            onClose={_onCloseOrMinimize}
            maximized={maximized}
            resizable={resizable}
            isFocus={isFocus}
          />
        </header>
      )}
      {header?.invisible && (
        <div className="app__floatingChrome">
          <HeaderButtons
            buttons={header.buttons}
            onMaximize={_onMouseUpMaximize}
            onMinimize={_onMouseUpMinimize}
            onClose={_onCloseOrMinimize}
            maximized={maximized}
            resizable={resizable}
            isFocus={isFocus}
          />
        </div>
      )}
      <div className="app__content">
        {component({
          onClose: _onMouseUpClose,
          onMinimize: _onMouseUpMinimize,
          isFocus,
          ...(header?.invisible ? { skinDragRef } : {}),
          ...injectProps,
        })}
      </div>
    </div>
  );
});

const windowEntry = keyframes`
  from {
    opacity: 0;
    scale: 0.97;
  }
  to {
    opacity: 1;
    scale: 1;
  }
`;

const StyledWindow = styled(Window)`
  display: ${({ show }) => (show ? 'flex' : 'none')};
  animation: ${({ show }) => show ? windowEntry : 'none'} 150ms cubic-bezier(0.23, 1, 0.32, 1) both;
  position: absolute;
  padding: 3px;
  padding: ${({ header }) => (header?.invisible ? 0 : 3)}px;
  background-color: ${({ isFocus, header }) =>
    header?.invisible ? 'transparent' : isFocus ? '#0831d9' : '#6582f5'};
  flex-direction: column;
  border-top-left-radius: ${({ header }) => (header?.invisible ? 0 : '8px')};
  border-top-right-radius: ${({ header }) => (header?.invisible ? 0 : '8px')};
  border-bottom-left-radius: ${({ header }) => (header?.invisible ? 0 : '8px')};
  border-bottom-right-radius: ${({ header }) => (header?.invisible ? 0 : '8px')};
  .header__bg {
    background: ${({ isFocus }) =>
      isFocus
        ? 'linear-gradient(to bottom,#0058ee 0%,#3593ff 4%,#288eff 6%,#127dff 8%,#036ffc 10%,#0262ee 14%,#0057e5 20%,#0054e3 24%,#0055eb 56%,#005bf5 66%,#026afe 76%,#0062ef 86%,#0052d6 92%,#0040ab 94%,#003092 100%)'
        : 'linear-gradient(to bottom, #7697e7 0%,#7e9ee3 3%,#94afe8 6%,#97b4e9 8%,#82a5e4 14%,#7c9fe2 17%,#7996de 25%,#7b99e1 56%,#82a9e9 81%,#80a5e7 89%,#7b96e1 94%,#7a93df 97%,#abbae3 100%)'};
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    height: 28px;
    pointer-events: none;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    overflow: hidden;
  }
  .header__bg:before {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    opacity: ${({ isFocus }) => (isFocus ? 1 : 0.3)};
    background: linear-gradient(to right, #1638e6 0%, transparent 100%);
    top: 0;
    bottom: 0;
    width: 15px;
  }
  .header__bg:after {
    content: '';
    opacity: ${({ isFocus }) => (isFocus ? 1 : 0.4)};
    display: block;
    position: absolute;
    right: 0;
    background: linear-gradient(to left, #1638e6 0%, transparent 100%);
    top: 0;
    bottom: 0;
    width: 15px;
  }
  .app__header {
    display: ${({ header }) => (header.invisible ? 'none' : 'flex')};
    height: 25px;
    line-height: 25px;
    font-weight: 700;
    font-size: 12px;
    font-family: 'Noto Sans';
    text-shadow: 1px 1px #000;
    color: white;
    position: absolute;
    left: 3px;
    right: 3px;
    align-items: center;
  }
  .app__header__icon {
    width: 15px;
    height: 15px;
    margin-left: 1px;
    margin-right: 3px;
  }
  .app__header__title {
    flex: 1;
    pointer-events: none;
    padding-right: 5px;
    letter-spacing: 0.5px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .app__content {
    flex: 1;
    position: relative;
    margin-top: ${({ header }) => (header?.invisible ? 0 : '25px')};
    height: ${({ header }) =>
      header?.invisible ? '100%' : 'calc(100% - 25px)'};
    min-height: 0;
  }
  .app__floatingChrome {
    position: absolute;
    top: 6px;
    right: 8px;
    z-index: 100;
    pointer-events: auto;
  }
`;

export default Windows;
